import { makeStyles, tokens, type GriffelStyle } from '@fluentui/react-components'

import { appTokens } from './theme'

type StyleFactory<T extends unknown[] = []> = (...args: T) => GriffelStyle

type StyleDictionary = Record<string, GriffelStyle | StyleFactory<never>>

function defineStyles<T extends StyleDictionary>(styles: T): T {
  return styles
}

/**
 * Shared layout definitions to avoid duplication
 */
const flexStyles = {
  flexRow2: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS },
  flexRow4: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  flexRow8: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  flexRow12: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  flexRow16: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalL },
  flexRow20: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXL },
  flexRow24: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXXL },
  flexRow32: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXXXL },
  flexColumn2: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  flexColumn4: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  flexColumn8: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  flexColumn12: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  flexColumn16: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  flexColumn20: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL },
  flexColumn24: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXL },
  flexColumn32: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXXL },
} satisfies Record<string, GriffelStyle>

const justifyStyles = {
  justifyStart: { justifyContent: 'flex-start' },
  justifyCenter: { justifyContent: 'center' },
  justifyEnd: { justifyContent: 'flex-end' },
  justifyBetween: { justifyContent: 'space-between' },
  justifyAround: { justifyContent: 'space-around' },
  justifyEvenly: { justifyContent: 'space-evenly' },
} satisfies Record<string, GriffelStyle>

const cardStyles = {
  cardSmall: {
    backgroundColor: appTokens.colorWhite,
    borderRadius: tokens.borderRadius2XLarge,
    boxShadow: tokens.shadow2,
    padding: tokens.spacingHorizontalL,
  },
  cardLarge: {
    backgroundColor: appTokens.colorWhite,
    borderRadius: tokens.borderRadius2XLarge,
    boxShadow: tokens.shadow2,
    padding: tokens.spacingHorizontalXXL,
  },
} satisfies Record<string, GriffelStyle>

const genericStyles = {
  overlay: {
    backgroundColor: tokens.colorBrandShadowAmbient,
    boxSizing: 'border-box',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    opacity: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    padding: tokens.spacingVerticalS,
    transition: 'opacity 0.2s ease-in-out',
    ':hover': {
      opacity: 1,
    },
  },
} satisfies Record<string, GriffelStyle>

/**
 * Common styles and utility classes for other griffel styles in the application
 */
const commonStyles = defineStyles({
  ...flexStyles,
  ...cardStyles,
  ...justifyStyles,
  ...genericStyles,
  preLine: { whiteSpace: 'pre-line' } satisfies GriffelStyle,
  overflowEllipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } satisfies GriffelStyle,
  maxLinesEllipsis: (lines: number): GriffelStyle => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
})

/**
 * App styles and utility classes for components across the application
 */
export const useAppStyles = makeStyles({
  ...flexStyles,
  ...cardStyles,
  ...justifyStyles,
  ...genericStyles,
  flex: { flex: 1 },
  pointer: { cursor: 'pointer' },
  spacer: { flex: '1 1 auto' },
  preLine: { whiteSpace: 'pre-line' },
  container: {
    width: '100%',
    minWidth: 'fit-content',
    maxWidth: '1440px',
    boxSizing: 'border-box',
    marginInline: 'auto',
    padding: tokens.spacingHorizontalXXL,
    paddingInline: 'clamp(16px, 2vw, 32px)',
  },
  hide: { display: 'none' },
  delete: {
    '--colorNeutralForeground2BrandHover': tokens.colorStatusDangerBackground3Hover,
    '--colorNeutralForeground1Hover': tokens.colorStatusDangerBackground3Hover,
    '--colorNeutralForeground1Pressed': tokens.colorStatusDangerBackground3Pressed,
    color: tokens.colorStatusDangerBackground3,
    '&:disabled': { color: tokens.colorNeutralForegroundDisabled },
  },
  deleteMenuItem: {
    '--colorNeutralForeground2BrandSelected': tokens.colorStatusDangerBackground3Hover,
    '--colorNeutralForeground2Hover': tokens.colorStatusDangerBackground3Hover,
    '--colorNeutralForeground2Pressed': tokens.colorStatusDangerBackground3Pressed,
    color: tokens.colorStatusDangerBackground3,
  },
  success: {
    '--colorNeutralForeground1Hover': tokens.colorStatusSuccessForeground1,
    '--colorNeutralForeground1Pressed': tokens.colorStatusSuccessForeground2,
    color: tokens.colorStatusSuccessBackground3,
    '&:disabled': { color: tokens.colorNeutralForegroundDisabled },
  },
  successMenuItem: {
    '--colorNeutralForeground2BrandSelected': tokens.colorStatusSuccessForeground1,
    '--colorNeutralForeground2Hover': tokens.colorStatusSuccessForeground1,
    '--colorNeutralForeground2Pressed': tokens.colorStatusSuccessForeground2,
    color: tokens.colorStatusSuccessBackground3,
  },
  overflowEllipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  overflowEllipsisMax2: { ...commonStyles.maxLinesEllipsis(2) },
  overflowEllipsisMax3: { ...commonStyles.maxLinesEllipsis(3) },
})

export default commonStyles
